// ═══════════════════════════════════════════════════════════════
// MainStreet — Organization Detail
// View / edit a single organization, related facilities & contacts
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, SimpleGrid, Tabs, Modal, Stack,
} from '@mantine/core';
import {
  IconPencil, IconTrash, IconArrowLeft, IconDeviceFloppy, IconX,
  IconBuilding, IconUser,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useMainStreetStore } from '../../store/mainstreetStore';
import type { MSOrganization } from '../../store/mainstreetStore';
import { SECTOR_TYPES, OP_STATUS } from './constants';
import { formatPhone } from '../../lib/phoneFormat';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const SECTOR_SELECT = SECTOR_TYPES.map(s => ({ value: s.id, label: s.label }));
const STATUS_SELECT = OP_STATUS.map(s => ({ value: s.id, label: s.label }));
const INSURANCE_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'insured', label: 'Insured' },
  { value: 'partial', label: 'Partial' },
  { value: 'uninsured', label: 'Uninsured' },
];
const CONTINUITY_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

function sectorLabel(id: string): string {
  return SECTOR_TYPES.find(s => s.id === id)?.label ?? id;
}
function opStatusEntry(id: string) {
  return OP_STATUS.find(s => s.id === id);
}

function LabelValue({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed" fw={500}>{label}</Text>
      <Text size="sm">{value || '-'}</Text>
    </div>
  );
}

export default function OrgDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    organizations, facilities, contacts,
    loadOrganizations, loadFacilities, loadContacts,
    updateOrganization, deleteOrganization,
  } = useMainStreetStore();

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // form state
  const [form, setForm] = useState<Partial<MSOrganization>>({});

  const org = useMemo(() =>
    organizations.find(o => o.id === Number(id)),
  [organizations, id]);

  const orgFacilities = useMemo(() =>
    facilities.filter(f => f.orgId === String(org?.id)),
  [facilities, org]);

  const orgContacts = useMemo(() =>
    contacts.filter(c => c.orgId === String(org?.id)),
  [contacts, org]);

  useEffect(() => {
    loadOrganizations();
    loadFacilities();
    loadContacts();
  }, []);

  useEffect(() => {
    if (org) setForm({ ...org });
  }, [org]);

  const setField = (key: keyof MSOrganization, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!org) return;
    setSaving(true);
    try {
      await updateOrganization(org.id, {
        ...form,
        lastVerified: new Date().toISOString(),
      });
      notifications.show({ message: 'Organization updated', color: 'green' });
      setEditing(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!org) return;
    setSaving(true);
    try {
      await deleteOrganization(org.id);
      notifications.show({ message: `${org.name} deleted`, color: 'gray' });
      navigate('/mainstreet/organizations');
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally {
      setSaving(false);
      setDeleteOpen(false);
    }
  };

  if (!org) {
    return (
      <div>
        <Button variant="subtle" leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate('/mainstreet/organizations')} mb="md">Back</Button>
        <Text c="dimmed">Organization not found.</Text>
      </div>
    );
  }

  const status = opStatusEntry(org.opStatus);

  return (
    <div>
      <Breadcrumbs items={[
        { label: 'Hub', to: '/' },
        { label: 'MainStreet', to: '/mainstreet' },
        { label: 'Organizations', to: '/mainstreet/organizations' },
        { label: org.name || 'Organization' },
      ]} />
      <Group justify="space-between" mb="md">
        <Group>
          <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
            onClick={() => navigate('/mainstreet/organizations')}>Back</Button>
          <Title order={3} c="black">{org.name || '(unnamed)'}</Title>
          {status && <Badge color={status.color}>{status.label}</Badge>}
        </Group>
        <Group gap="xs">
          {editing ? (
            <>
              <Button size="xs" variant="light" color="gray" leftSection={<IconX size={14} />}
                onClick={() => { setEditing(false); setForm({ ...org }); }}>Cancel</Button>
              <Button size="xs" leftSection={<IconDeviceFloppy size={14} />}
                loading={saving} onClick={handleSave}>Save</Button>
            </>
          ) : (
            <>
              <Button size="xs" variant="light" leftSection={<IconPencil size={14} />}
                onClick={() => setEditing(true)}>Edit</Button>
              <Button size="xs" variant="light" color="red" leftSection={<IconTrash size={14} />}
                onClick={() => setDeleteOpen(true)}>Delete</Button>
            </>
          )}
        </Group>
      </Group>

      <Paper withBorder p="md" mb="lg">
        {editing ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
            <TextInput label="Name" value={form.name ?? ''} onChange={e => setField('name', e.currentTarget.value)} />
            <Select label="Sector" data={SECTOR_SELECT} value={form.sectorType ?? ''} onChange={v => setField('sectorType', v ?? '')} clearable />
            <TextInput label="Address" value={form.address ?? ''} onChange={e => setField('address', e.currentTarget.value)} />
            <TextInput label="City" value={form.city ?? ''} onChange={e => setField('city', e.currentTarget.value)} />
            <TextInput label="State" value={form.state ?? ''} onChange={e => setField('state', e.currentTarget.value)} />
            <TextInput label="ZIP" value={form.zip ?? ''} onChange={e => setField('zip', e.currentTarget.value)} />
            <TextInput label="Phone" value={form.phone ?? ''} onChange={e => setField('phone', e.currentTarget.value)} />
            <TextInput label="Email" value={form.email ?? ''} onChange={e => setField('email', e.currentTarget.value)} />
            <TextInput label="Primary Contact" value={form.primaryContact ?? ''} onChange={e => setField('primaryContact', e.currentTarget.value)} />
            <Select label="Operational Status" data={STATUS_SELECT} value={form.opStatus ?? ''} onChange={v => setField('opStatus', v ?? '')} clearable />
            <Select label="Insurance" data={INSURANCE_OPTIONS} value={form.insurance ?? ''} onChange={v => setField('insurance', v ?? '')} clearable />
            <Select label="Continuity Plan" data={CONTINUITY_OPTIONS} value={form.continuity ?? ''} onChange={v => setField('continuity', v ?? '')} clearable />
            <Textarea label="Notes" value={form.notes ?? ''} onChange={e => setField('notes', e.currentTarget.value)} autosize minRows={2} style={{ gridColumn: '1 / -1' }} />
          </SimpleGrid>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
            <LabelValue label="Name" value={org.name} />
            <LabelValue label="Sector" value={sectorLabel(org.sectorType)} />
            <LabelValue label="Address" value={org.address} />
            <LabelValue label="City" value={org.city} />
            <LabelValue label="State" value={org.state} />
            <LabelValue label="ZIP" value={org.zip} />
            <LabelValue label="Phone" value={org.phone ? formatPhone(org.phone) : ''} />
            <LabelValue label="Email" value={org.email} />
            <LabelValue label="Primary Contact" value={org.primaryContact} />
            <LabelValue label="Status" value={status?.label ?? org.opStatus} />
            <LabelValue label="Insurance" value={org.insurance} />
            <LabelValue label="Continuity Plan" value={org.continuity} />
            <LabelValue label="Last Verified" value={org.lastVerified ? new Date(org.lastVerified).toLocaleDateString() : ''} />
            {org.notes && <div style={{ gridColumn: '1 / -1' }}><LabelValue label="Notes" value={org.notes} /></div>}
          </SimpleGrid>
        )}
      </Paper>

      <Tabs defaultValue="facilities">
        <Tabs.List mb="sm">
          <Tabs.Tab value="facilities" leftSection={<IconBuilding size={14} />}>
            Facilities ({orgFacilities.length})
          </Tabs.Tab>
          <Tabs.Tab value="contacts" leftSection={<IconUser size={14} />}>
            Contacts ({orgContacts.length})
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="facilities">
          <Paper withBorder>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Type</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Address</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Generator</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Hazmat</th>
                </tr>
              </thead>
              <tbody>
                {orgFacilities.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#888' }}>No facilities</td></tr>
                )}
                {orgFacilities.map(f => (
                  <tr key={f.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                    <td style={{ padding: '8px 12px' }}>{f.name || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{f.type || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{[f.address, f.city, f.state].filter(Boolean).join(', ') || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge size="xs" color={f.hasGen === 'true' ? 'green' : 'gray'}>{f.hasGen === 'true' ? 'Yes' : 'No'}</Badge>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge size="xs" color={f.hasHazmat === 'true' ? 'red' : 'gray'}>{f.hasHazmat === 'true' ? 'Yes' : 'No'}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="contacts">
          <Paper withBorder>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Title</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Type</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Email</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Phone</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Auth</th>
                </tr>
              </thead>
              <tbody>
                {orgContacts.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 16, textAlign: 'center', color: '#888' }}>No contacts</td></tr>
                )}
                {orgContacts.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                    <td style={{ padding: '8px 12px' }}>{c.name || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{c.title || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{c.type || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{c.email || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{c.phone || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge size="xs" color={c.canAuth === 'true' ? 'green' : 'gray'}>{c.canAuth === 'true' ? 'Yes' : '-'}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Paper>
        </Tabs.Panel>
      </Tabs>

      {/* Delete confirmation */}
      <Modal opened={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete Organization" centered size="sm">
        <Stack>
          <Text size="sm">Are you sure you want to delete <strong>{org.name}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
